import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card } from "./ui";
import { KanbanSquare, ArrowUpRight, AlertTriangle, RefreshCw } from "lucide-react";

// Cross-entity project delivery rollup for the Group HQ (open / done / overdue per entity).
// Hidden for scoped users (403) and when there are no board items; shows a retry on transient errors.
export default function ProjectDeliveryWidget() {
  const { t, entities } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [errored, setErrored] = useState(false);

  const load = useCallback(() => {
    setErrored(false);
    api.get("/reports/overview")
      .then(({ data }) => setData(data))
      .catch((e) => { if (e?.response?.status === 403) setHidden(true); else setErrored(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  if (hidden) return null;
  if (errored && !data) {
    return (
      <Card className="mb-6 flex items-center justify-between" data-testid="project-delivery-error">
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <AlertTriangle className="w-4 h-4 text-amber-400" />{t("report_delivery_error")}
        </div>
        <button onClick={load} data-testid="delivery-retry" className="inline-flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary">
          <RefreshCw className="w-3.5 h-3.5" />{t("retry") || "Retry"}
        </button>
      </Card>
    );
  }
  if (!data) return null;
  if (!data.totals.total) return null;

  const gotoEntity = (eid) => { const e = entities.find((x) => x.id === eid); if (e) navigate(`/entity/${e.key}`); };
  const rows = data.entities.filter((r) => r.total > 0);

  return (
    <Card className="mb-6" data-testid="project-delivery-widget">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <KanbanSquare className="w-4 h-4 text-text-secondary" />
          <h3 className="font-heading font-medium">{t("report_delivery_title")}</h3>
        </div>
        <div className="flex items-center gap-4 text-xs text-text-secondary">
          <span><span className="font-medium text-text-primary tabular-nums">{data.totals.open}</span> {t("report_open").toLowerCase()}</span>
          {data.totals.overdue > 0 && (
            <span className="inline-flex items-center gap-1 text-red-400"><AlertTriangle className="w-3.5 h-3.5" />{data.totals.overdue} {t("report_overdue").toLowerCase()}</span>
          )}
          <span><span className="font-medium text-text-primary tabular-nums">{data.totals.completion_pct}%</span> {t("report_done").toLowerCase()}</span>
        </div>
      </div>
      <div className="space-y-4">
        {rows.map((r) => (
          <button key={r.entity_id} onClick={() => gotoEntity(r.entity_id)} data-testid={`delivery-row-${r.entity_id}`} className="w-full text-left group">
            <div className="flex items-center justify-between mb-1.5">
              <span className="flex items-center gap-2 text-sm font-medium">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: r.color }} />
                {r.name}
                <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-text-secondary" />
              </span>
              <span className="flex items-center gap-3 text-xs tabular-nums">
                {r.overdue > 0 && <span className="text-red-400">{r.overdue} {t("overdue")}</span>}
                <span className="text-text-secondary">{r.done}/{r.total}</span>
                <span className="w-9 text-right font-medium">{r.completion_pct}%</span>
              </span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--muted)" }}>
              <div className="h-full rounded-full transition-all duration-500 group-hover:opacity-80"
                style={{ width: `${r.completion_pct}%`, backgroundColor: r.color }} />
            </div>
          </button>
        ))}
      </div>
    </Card>
  );
}
